import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { ProductBusinessProfileImage } from '../business/BusinessProfileImage';
import { createCompanyFirstUrl } from '../../utils/urlUtils';
import { getLocationCardShortLabel, getLocationDisplayLabel } from '../../data/iranLocations';
import { apiUrl } from '../../config/api';
import { resolveCatalogSort } from '../../utils/catalogSort';
import useCatalogGridColumns from '../../hooks/useCatalogGridColumns';
import {
  LANDING_CATALOG_FETCH_LIMIT,
  getLandingCatalogVisibleCount,
} from '../../config/landingCatalog';
import { ProductCatalogSkeleton } from '../products/CatalogSkeletons';
import ProductsEmptyState from '../products/ProductsEmptyState';
import { getBusinessType, BUSINESS_TYPE_I18N_KEYS } from '../../config/businessTypes';

export const ALL_IRAN_CITY = 'کل ایران';

function hasActiveCatalogFilters(searchParams) {
  return Boolean(
    searchParams.search?.trim()
    || (searchParams.city && searchParams.city !== ALL_IRAN_CITY)
    || (searchParams.serviceType && searchParams.serviceType !== 'all')
    || (searchParams.rating && searchParams.rating !== 'all')
    || (searchParams.history && searchParams.history !== 'all')
  );
}

const ServiceCard = ({ service, user }) => {
  const { t, i18n } = useTranslation();
  const locationLabelFull = getLocationDisplayLabel(service.city);
  const locationShort = getLocationCardShortLabel(service.city);
  const businessTypeConfig = getBusinessType(service.businessId?.businessType);
  const businessTypeKey = BUSINESS_TYPE_I18N_KEYS[service.businessId?.businessType];
  const businessTypeDisplayLabel = businessTypeKey
    ? t(businessTypeKey)
    : null;
  const numberLocale =
    i18n.language?.startsWith('fa') ? 'fa-IR' : i18n.language?.startsWith('ar') ? 'ar-EG' : 'en-US';

  return (
  <Link
    to={createCompanyFirstUrl(service, service.businessId)}
    className="group flex h-full min-h-0 rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/35 focus-visible:ring-offset-2 focus-visible:ring-offset-linen-canvas"
  >
    <div className="listing-product-card relative flex h-full min-h-80 flex-1 flex-col overflow-hidden rounded-3xl border border-hairline/25 bg-card-surface p-4 shadow-feature transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] before:pointer-events-none before:absolute before:inset-0 before:rounded-3xl before:ring-1 before:ring-inset before:ring-ink-midnight/[0.04] hover:-translate-y-0.5 hover:border-hairline/45 hover:shadow-[0_20px_50px_-24px_rgba(15,23,42,0.18),0_1px_0_0_rgba(255,255,255,0.7)_inset] sm:p-5">
      <div className="relative">
        <div className="listing-product-card__media relative mb-3 aspect-square w-full overflow-hidden rounded-2xl bg-gradient-to-br from-sky-wash via-card-surface to-sky-wash ring-1 ring-ink-midnight/[0.05]">
          {service.images && service.images.length > 0 && service.images[0] ? (
            <img 
              src={service.images[0]}
              alt={service.name} 
              className="product-image h-full w-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]" 
              onError={(e) => {
                console.error('Error loading product image:', {
                  productName: service.name,
                  productId: service._id,
                  imageUrl: e.target.src,
                  allImages: service.images,
                  imageCount: service.images.length,
                  error: 'Image failed to load'
                });
                // تلاش برای استفاده از عکس پیش‌فرض
                e.target.src = '/service1.webp';
                e.target.onerror = null;
              }}
              crossOrigin="anonymous"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <div className="text-center text-gray-500">
                <div className="text-4xl mb-2">📷</div>
                <div className="text-sm">{t('services.noImage')}</div>
                <div className="text-xs mt-1">
                  {service.images?.length
                    ? t('services.photoCount', { count: service.images.length })
                    : t('services.noPhotos')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* نشانگر محصول جدید - فقط برای محصولات کمتر از 5 دقیقه */}
        {isNewProduct(service.createdAt) && (
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full border border-white/60 bg-white/90 px-2 py-1 text-caption font-semibold text-coral-alert shadow-sm backdrop-blur-sm supports-[backdrop-filter]:bg-white/75">
            <span className="h-1.5 w-1.5 rounded-full bg-coral-alert" />
            {t('services.newBadge')}
          </div>
        )}
      </div>

      {/* اطلاعات محصول — فاصله‌های فشرده؛ متا در یک ستون زیر هم‌تراز با نام */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 pt-0.5">
        <h3
          className="overflow-hidden text-heading-sm font-semibold tracking-[-0.02em] text-ink-graphite transition-colors group-hover:text-ink-midnight"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            lineHeight: '1.3'
          }}
        >
          {service.name}
        </h3>

        {/* ردیف کسب‌وکار: آواتار + ستون نام/برچسب‌ها (برچسب‌ها زیر نام، هم‌مرز با متن) */}
        <div className="flex min-h-0 items-start gap-2.5">
          <ProductBusinessProfileImage
            businessData={service.businessId}
            currentUser={user}
            size="sm"
            className="mt-0.5 flex-shrink-0 shadow-sm ring-2 ring-white"
          />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate text-sm font-semibold leading-snug text-ink-graphite">
              {service.businessId?.companyName || t('services.unknown')}
            </p>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {businessTypeDisplayLabel && (
                <span
                  className={`inline-flex flex-shrink-0 items-center rounded-md border px-2 py-px text-[11px] font-semibold leading-tight ${businessTypeConfig?.badgeClass ?? ''}`}
                  title={t('services.businessTypeTitle', { type: businessTypeDisplayLabel })}
                >
                  {businessTypeDisplayLabel}
                </span>
              )}
              <span
                className="inline-flex max-w-full min-w-0 items-center rounded-md border border-hairline/35 bg-sky-wash/60 px-2 py-px text-[11px] font-semibold leading-tight text-steel"
                title={locationLabelFull}
              >
                <span className="truncate">{locationShort}</span>
              </span>
            </div>
          </div>
        </div>

        {/* قیمت: بدون مونواسپیس (خوانایی فارسی)؛ نوار پایین تمام‌عرض */}
        {service.price && user && (
          <div className="listing-product-card__price mt-auto -mx-4 flex flex-col border-t border-hairline/25 bg-gradient-to-b from-sky-wash/25 to-transparent px-4 pt-2.5 sm:-mx-5 sm:px-5 sm:pt-3">
            <div className="flex w-full flex-wrap items-baseline justify-end gap-x-2 gap-y-0.5 text-end">
              <span className="text-[17px] font-bold tabular-nums tracking-[-0.03em] text-ink-midnight [font-variant-numeric:tabular-nums]">
                {new Intl.NumberFormat(numberLocale).format(service.price)}
              </span>
              <span className="text-caption font-medium text-ash">{t('traderPublic.currency')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  </Link>
  );
};

// تابع تشخیص محصول جدید (کمتر از 5 دقیقه)
const isNewProduct = (createdAt) => {
  const now = new Date();
  const productDate = new Date(createdAt);
  const diffTime = Math.abs(now - productDate);
  const diffMinutes = Math.ceil(diffTime / (1000 * 60));
  return diffMinutes <= 5;
};

const defaultSearchParams = {
  search: '',
  city: ALL_IRAN_CITY,
  serviceType: 'all',
  rating: 'all',
  history: 'all'
};

const ServicesSection = ({ searchParams: searchParamsProp, catalogLimit = 12, variant = 'default' }) => {
  useNotification();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isLanding = variant === 'landing';
  const gridColumns = useCatalogGridColumns();
  const landingVisibleCount = getLandingCatalogVisibleCount(gridColumns);
  const effectiveCatalogLimit = isLanding ? LANDING_CATALOG_FETCH_LIMIT : catalogLimit;
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalItems] = useState(0);
  const [internalSearchParams, setInternalSearchParams] = useState({});
  const searchParams = useMemo(() => (
    searchParamsProp !== undefined && searchParamsProp !== null
      ? { ...defaultSearchParams, ...searchParamsProp }
      : { ...defaultSearchParams, ...internalSearchParams }
  ), [searchParamsProp, internalSearchParams]);
  const [, setLastUpdate] = useState(null);
  const [, setTotalNewProducts] = useState(0);

  // دریافت پارامترهای جستجو از localStorage (فقط وقتی prop از parent نمی‌آید)
  useEffect(() => {
    if (searchParamsProp !== undefined && searchParamsProp !== null) return;
    const savedParams = localStorage.getItem('searchParams');
    if (savedParams) {
      try {
        const params = JSON.parse(savedParams);
        setInternalSearchParams(params);
      } catch (e) {
        console.error('Error parsing saved search params:', e);
      }
    }
  }, [searchParamsProp]);

  // گوش دادن به تغییرات کاربر
  useEffect(() => {
    console.log('User changed in ServicesSection:', {
      user: user,
      userProfileImage: user?.profileImage,
      sessionStorageProfileImage: sessionStorage.getItem('profileImage')
    });
  }, [user]);

  // گوش دادن به تغییرات sessionStorage و custom events
  useEffect(() => {
    const handleStorageChange = () => {
      const profileImage = sessionStorage.getItem('profileImage');
      console.log('SessionStorage profile image changed:', profileImage);
      // Force re-render by updating a state
      setLastUpdate(new Date());
    };

    const handleProfileImageUpdate = (event) => {
      console.log('ServicesSection: Profile image updated event received:', event.detail);
      // Force re-render by updating a state
      setLastUpdate(new Date());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
    };
  }, []);

  // گوش دادن به تغییرات جستجو (فقط وقتی prop از parent نمی‌آید)
  useEffect(() => {
    if (searchParamsProp !== undefined && searchParamsProp !== null) return;
    const handleSearchParamsChanged = (event) => {
      setInternalSearchParams(event.detail);
      setCurrentPage(1);
    };

    window.addEventListener('searchParamsChanged', handleSearchParamsChanged);
    return () => {
      window.removeEventListener('searchParamsChanged', handleSearchParamsChanged);
    };
  }, [searchParamsProp]);

  // دریافت محصولات از API
  const fetchServices = async (page = 1) => {
    try {
      setLoading(true);
      
      // ساخت URL با پارامترهای جستجو
      const params = new URLSearchParams({
        page: page,
        limit: String(effectiveCatalogLimit)
      });

      if (searchParams.search) {
        params.append('search', searchParams.search);
      }
      if (searchParams.city && searchParams.city !== ALL_IRAN_CITY) {
        params.append('city', searchParams.city);
      }

      const { sortBy, sortOrder } = resolveCatalogSort(searchParams);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      if (searchParams.serviceType && searchParams.serviceType !== 'all') {
        params.append('serviceType', searchParams.serviceType);
      }

      const listUrl = apiUrl(`/api/product/public/all?${params}`);

      const response = await fetch(listUrl);
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setServices(result.data || []);
          setTotalPages(result.pagination?.totalPages ?? 1);
          setTotalItems(result.pagination?.totalItems ?? 0);
          // همچنان اطلاعات real-time را دریافت می‌کنیم ولی نمایش نمی‌دهیم
          setLastUpdate(result.realTime?.lastUpdate || new Date());
        } else {
          console.error('🔍 ServicesSection: API error:', result.message);
          setServices([]);
        }
      } else {
        console.error('🔍 ServicesSection: HTTP error:', response.status);
        setServices([]);
      }
    } catch (error) {
      console.error('🔍 ServicesSection: Fetch error:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // دریافت محصولات — با تغییر فیلتر، صفحه به ۱ برمی‌گردد
  const filterKey = useMemo(() => JSON.stringify(searchParams), [searchParams]);
  const prevFilterKeyRef = React.useRef(filterKey);

  useEffect(() => {
    const filtersChanged = prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;
    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    fetchServices(currentPage);
  }, [filterKey, currentPage, effectiveCatalogLimit]);

  // Polling برای دریافت محصولات جدید — فقط صفحهٔ اول، بدون فیلتر، تب فعال
  useEffect(() => {
    if (isLanding) return undefined;

    const pollMs = 120000;
    const shouldPoll = () =>
      document.visibilityState === 'visible' &&
      currentPage === 1 &&
      (!searchParams.search || searchParams.search === '') &&
      searchParams.city === ALL_IRAN_CITY &&
      searchParams.serviceType === 'all' &&
      searchParams.rating === 'all' &&
      searchParams.history === 'all';

    const interval = setInterval(() => {
      if (shouldPoll()) {
        fetchServices(1);
      }
    }, pollMs);

    const onVisibilityChange = () => {
      if (shouldPoll()) {
        fetchServices(1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentPage, searchParams, effectiveCatalogLimit, isLanding]);

  // تغییر صفحه
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    const skeletonCount = isLanding ? landingVisibleCount : 8;
    const skeletonWrap = (
      <div className={`mx-auto w-full max-w-page px-4 sm:px-6 lg:px-8 ${isLanding ? 'pb-16 pt-0 md:pb-20' : 'pb-16 pt-2'}`}>
        {isLanding ? (
          <ProductCatalogSkeleton count={skeletonCount} />
        ) : (
          <div className="rounded-cardlg border border-hairline/40 bg-card-surface p-4 shadow-saas sm:p-6 lg:p-8">
            <ProductCatalogSkeleton count={skeletonCount} />
          </div>
        )}
      </div>
    );

    return (
      <div className={`bg-transparent ${isLanding ? 'pb-20 pt-0 md:pb-24' : 'pb-14 pt-2'}`}>
        {skeletonWrap}
      </div>
    );
  }

  if (services.length === 0) {
    if (isLanding) {
      return (
        <div className="mx-auto w-full max-w-page px-4 pb-16 pt-0 sm:px-6 md:pb-20 lg:px-8">
          <ProductsEmptyState />
        </div>
      );
    }

    const emptyContent = hasActiveCatalogFilters(searchParams) ? (
      <div className="py-6 text-center">
        <p className="type-card-title mb-2 text-xl">{t('services.filteredEmptyTitle')}</p>
        <p className="text-steel">{t('services.filteredEmptyDescription')}</p>
      </div>
    ) : (
      <ProductsEmptyState />
    );

    return (
      <div className="mx-auto w-full max-w-page px-4 pb-16 pt-2 sm:px-6 lg:px-8">
        <div className="rounded-cardlg border border-hairline/40 bg-card-surface p-4 shadow-saas sm:p-6 lg:p-8">
          {emptyContent}
        </div>
      </div>
    );
  }

  const visibleServices = isLanding ? services.slice(0, landingVisibleCount) : services;

  return (
    <div className={`mx-auto w-full max-w-page px-4 sm:px-6 lg:px-8 ${isLanding ? 'pb-16 pt-0 md:pb-20' : 'pb-16 pt-2'}`}>
      {isLanding ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8">
          {visibleServices.map((service) => (
            <ServiceCard key={service._id} service={service} user={user} />
          ))}
        </div>
      ) : (
        <>
          <div className="rounded-cardlg border border-hairline/40 bg-card-surface p-4 shadow-saas sm:p-6 lg:p-8">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8">
              {visibleServices.map((service) => (
                <ServiceCard key={service._id} service={service} user={user} />
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded-input border border-hairline bg-card-surface px-4 py-2 text-sm text-ink-graphite transition-colors hover:bg-sky-wash disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('services.prevPage')}
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`rounded-input px-4 py-2 text-sm transition-colors ${
                    currentPage === page
                      ? 'border-2 border-action bg-card-surface font-semibold text-action'
                      : 'border border-hairline bg-card-surface text-ink-graphite hover:bg-sky-wash'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="rounded-input border border-hairline bg-card-surface px-4 py-2 text-sm text-ink-graphite transition-colors hover:bg-sky-wash disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('services.nextPage')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ServicesSection;